import type { PainelPermission, PainelRole } from '@/lib/painel-rbac';
import {
  DEV_AUTH_TOKEN,
  clearDevSession,
  isDevAuthEnabled,
  isDevAuthToken,
  readDevSession,
  tryDevLogin,
} from '@/lib/dev-auth';
import { ApiError, apiDelete, apiGet, apiPost, apiPut, clearTokens, getToken, setTokens } from './api-client';

export type AuthUser = {
  id: number | string;
  username: string;
  name: string;
  email: string;
  role: PainelRole;
  permissions: PainelPermission[];
  status: 'active' | 'inactive';
  avatar_url?: string | null;
  official_xat_role?: string | null;
};

export type LoginResponse = {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: AuthUser;
};

export type AdminSession = {
  username: string;
  role: PainelRole;
  name: string;
  email: string;
  permissions: PainelPermission[];
  avatarUrl?: string | null;
};

export const ADMIN_LOGIN_PATH = '/admin/login';
export const ADMIN_DASHBOARD_PATH = '/painel';

export function mapUserToSession(user: AuthUser): AdminSession {
  return {
    username: user.username,
    role: user.role,
    name: user.name,
    email: user.email,
    permissions: user.permissions ?? [],
    avatarUrl: user.avatar_url ?? null,
  };
}

export async function login(
  username: string,
  password: string,
  remember = true
): Promise<AdminSession> {
  if (isDevAuthEnabled()) {
    try {
      const result = await apiPost<LoginResponse>('/auth/login', {
        username,
        password,
        remember,
      });
      clearDevSession();
      setTokens(result.access_token, { remember, expiresIn: result.expires_in });
      return mapUserToSession(result.user);
    } catch (err) {
      const devSession = tryDevLogin(username, password);
      if (devSession) {
        setTokens(DEV_AUTH_TOKEN, { remember });
        return devSession;
      }
      throw toLoginError(err);
    }
  }

  const result = await apiPost<LoginResponse>('/auth/login', {
    username,
    password,
    remember,
  });
  setTokens(result.access_token, { remember, expiresIn: result.expires_in });
  return mapUserToSession(result.user);
}

function toLoginError(err: unknown): Error {
  if (err instanceof ApiError) {
    return new Error(err.message || 'Usuário ou senha incorretos.');
  }
  if (err instanceof Error && err.message) {
    return err;
  }
  return new Error('Usuário ou senha incorretos.');
}

export async function me(): Promise<AdminSession | null> {
  const token = getToken();
  if (isDevAuthToken(token)) {
    return readDevSession();
  }

  try {
    const user = await apiGet<AuthUser>('/auth/me', true);
    return mapUserToSession(user);
  } catch {
    return null;
  }
}

export async function logout(): Promise<void> {
  const wasDev = isDevAuthToken(getToken());
  clearDevSession();
  clearTokens();
  if (!wasDev) {
    try {
      await apiPost('/auth/logout', undefined, true);
    } catch {
      // ignore
    }
  }
}

export async function changePassword(
  currentPassword: string,
  newPassword: string
): Promise<void> {
  if (isDevAuthToken(getToken())) {
    throw new Error('A alteração de senha não está disponível na sessão de desenvolvimento.');
  }
  try {
    await apiPut('/auth/password', {
      current_password: currentPassword,
      password: newPassword,
    }, true);
  } catch (err) {
    if (err instanceof ApiError) {
      throw new Error(err.message || 'Não foi possível alterar a senha.');
    }
    throw err;
  }
}

export async function uploadAvatar(file: File): Promise<AdminSession> {
  if (isDevAuthToken(getToken())) {
    throw new Error('O upload de avatar não está disponível na sessão de desenvolvimento.');
  }
  const form = new FormData();
  form.append('avatar', file);
  try {
    const user = await apiPost<AuthUser>('/auth/avatar', form, true);
    return mapUserToSession(user);
  } catch (err) {
    if (err instanceof ApiError) {
      throw new Error(err.message || 'Não foi possível enviar o avatar.');
    }
    throw err;
  }
}

export async function removeAvatar(): Promise<AdminSession> {
  if (isDevAuthToken(getToken())) {
    throw new Error('A remoção de avatar não está disponível na sessão de desenvolvimento.');
  }
  try {
    const user = await apiDelete<AuthUser>('/auth/avatar', true);
    return mapUserToSession(user);
  } catch (err) {
    if (err instanceof ApiError) {
      throw new Error(err.message || 'Não foi possível remover o avatar.');
    }
    throw err;
  }
}

export function isAuthenticated(): boolean {
  if (typeof window === 'undefined') return false;
  const token = getToken();
  if (!token) return false;
  if (isDevAuthToken(token)) return !!readDevSession();
  return true;
}
